import React, { Component, ReactNode } from 'react';
import { Box, Container, Typography, Button, Stack, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ErrorOutline } from '@mui/icons-material';
import PageHeader from './PageHeader';
import LoginFooter from '../layouts/LoginFooter';
import { useBrandConfig } from '../stores/brandConfigContext';
import { MarcaConfig } from '../utils/types';

interface ErrorBoundaryClassProps {
  children: ReactNode;
  fallback?: ReactNode;
  brandConfig: MarcaConfig | null;
  theme: Theme;
  brandKeyword: string;
}

interface ErrorBoundaryClassState {
  hasError: boolean;
  error?: Error;
}

// Class component necesario para getDerivedStateFromError
class ErrorBoundaryClass extends Component<ErrorBoundaryClassProps, ErrorBoundaryClassState> {
  constructor(props: ErrorBoundaryClassProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryClassState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Siempre loggear errores críticos
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    
    // Si el error indica sesión expirada, redirigir al login en lugar de mostrar error
    const errorAny = error as any;
    if (errorAny.status === 401 || errorAny.status === 403 || errorAny.requiresLogout) {
      console.warn('[ErrorBoundary] Error de sesión detectado, redirigiendo al login');
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const brandKw = pathParts[0] || 'acme';
      window.location.href = `/${brandKw}`;
      return;
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      const { brandConfig, theme, brandKeyword } = this.props;
      const nombreMarca = brandConfig?.nombre || 'LukaPay';
      const loginPath = brandKeyword ? `/${brandKeyword}` : '/acme';

      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.default
          }}
        >
          <PageHeader
            marcaInfo={brandConfig ? {
              logo: brandConfig.logo,
              logoDark: brandConfig.logoDark ?? undefined,
              nombre: brandConfig.nombre
            } : undefined}
            nombreMarca={nombreMarca}
            showCloseButton={true}
            onCloseClick={this.handleReload}
            onLogoClick={this.handleReload}
          />

          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Container maxWidth="sm">
              <Stack spacing={4} alignItems="center" textAlign="center">
                <ErrorOutline sx={{ fontSize: 80, color: theme.palette.error.main }} />
                
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.text.primary 
                  }}
                >
                  Algo salió mal
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.palette.text.secondary, 
                    maxWidth: 400 
                  }}
                >
                  Ha ocurrido un error inesperado. Por favor, recarga la página para continuar.
                </Typography>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={this.handleReload}
                  sx={{ 
                    textTransform: 'none', 
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  Recargar página
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.location.href = loginPath}
                  sx={{ 
                    textTransform: 'none', 
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  Volver al inicio
                </Button>
              </Stack>
              </Stack>
            </Container>
          </Box>

          <LoginFooter />
        </Box>
      );
    }

    return this.props.children;
  }
}

// Wrapper funcional que provee contexto al class component
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, fallback }) => {
  const { config: brandConfig } = useBrandConfig();
  const theme = useTheme();
  
  // Extraer brandKeyword de la URL actual como fallback
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const brandKeyword = pathParts.length > 0 ? pathParts[0] : 'acme';
  
  return (
    <ErrorBoundaryClass 
      brandConfig={brandConfig} 
      theme={theme}
      brandKeyword={brandKeyword}
      fallback={fallback}
    >
      {children}
    </ErrorBoundaryClass>
  );
};

export default ErrorBoundary;
